import { z } from 'zod';
import { dateDmySchema, uploadKeySchema } from './primitives.js';

export const DOC_KINDS = [
  'photoId',
  'proofAddress',
  'rightToWork',
  'niProof',
  'nameChange',
] as const;
export type DocKind = (typeof DOC_KINDS)[number];

export const PHOTO_ID_TYPES = ['Passport', 'Driving Licence', 'Other Photo ID'] as const;
export const PROOF_ADDRESS_TYPES = ['Utility Bill', 'Bank Statement', 'Council Tax Letter'] as const;
export const RIGHT_TO_WORK_TYPES = ['Passport', 'Share Code', 'BRP Card', 'Visa'] as const;
export const NI_PROOF_TYPES = ['P60', 'Payslip', 'NI Card / Letter'] as const;
export const NAME_CHANGE_TYPES = ['Deed Poll', 'Marriage Certificate', 'Other'] as const;

const docItem = <T extends readonly [string, ...string[]]>(types: T) =>
  z.object({
    type: z.enum(types),
    objectKey: uploadKeySchema,
  });

export const evisaSchema = z.object({
  shareCode: z.string().trim().min(6).max(20),
  dob: dateDmySchema,
  passed: z.boolean(),
  details: z.record(z.unknown()).optional(),
  checkedAt: z.string().datetime(),
});

export const documentsSchema = z
  .object({
    photoId: docItem(PHOTO_ID_TYPES).optional(),
    proofAddress: docItem(PROOF_ADDRESS_TYPES).optional(),
    rightToWork: docItem(RIGHT_TO_WORK_TYPES).optional(),
    niProof: docItem(NI_PROOF_TYPES).optional(),
    nameChange: z.array(docItem(NAME_CHANGE_TYPES)).max(10).optional(),
    evisa: evisaSchema.optional(),
  })
  .superRefine((val, ctx) => {
    const evisaPassed = val.evisa?.passed === true;
    if (evisaPassed) return;
    for (const k of ['photoId', 'proofAddress', 'rightToWork'] as const) {
      if (!val[k]) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [k],
          message: `${k} is required when no valid eVisa is provided`,
        });
      }
    }
  });

export type Documents = z.infer<typeof documentsSchema>;
